import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { RequiredEnrollmentDocument } from '@/api/enrollment-required-documents'
import { SELF_ENROLLMENT_STEPS } from '../../utils/steps'

type UseEnrollmentBackActionArgs = {
  consumeUploadDocumentsSkipped: () => boolean
  currentStep: number
  enrollmentSessionToken: string | null
  goToStep: (step: number) => void
  resetAnonymousEnrollmentAndReturnToOptions: () => void
  setSessionStepSyncLock: (step: number | null) => void
  suppressNextSessionStepSync: () => void
}

export function useEnrollmentBackAction({
  consumeUploadDocumentsSkipped,
  currentStep,
  enrollmentSessionToken,
  goToStep,
  resetAnonymousEnrollmentAndReturnToOptions,
  setSessionStepSyncLock,
  suppressNextSessionStepSync
}: UseEnrollmentBackActionArgs) {
  const queryClient = useQueryClient()

  return useCallback(() => {
    suppressNextSessionStepSync()

    const requiredDocumentsQueryKey = enrollmentSessionToken
      ? (['enrollment', 'required-documents', enrollmentSessionToken] as const)
      : null
    const requiredDocuments = requiredDocumentsQueryKey
      ? queryClient.getQueryData<RequiredEnrollmentDocument[]>(requiredDocumentsQueryKey)
      : undefined
    const hasResolvedRequiredDocuments = Array.isArray(requiredDocuments)
    const hasVisibleUploadDocuments = (requiredDocuments ?? []).some(
      document => document.documentType !== 'DRIVERS_LICENSE'
    )

    if (
      (currentStep === SELF_ENROLLMENT_STEPS.SIGN_DOCUMENTS ||
        currentStep === SELF_ENROLLMENT_STEPS.SIGNED_COMPLETE) &&
      (consumeUploadDocumentsSkipped() ||
        (hasResolvedRequiredDocuments && !hasVisibleUploadDocuments))
    ) {
      setSessionStepSyncLock(SELF_ENROLLMENT_STEPS.TRAINING_DATE)
      goToStep(SELF_ENROLLMENT_STEPS.TRAINING_DATE)
      return
    }

    if (currentStep === SELF_ENROLLMENT_STEPS.SIGNED_COMPLETE) {
      const previousStep = SELF_ENROLLMENT_STEPS.UPLOAD_DOCUMENTS
      setSessionStepSyncLock(previousStep)
      goToStep(previousStep)
      return
    }

    if (
      currentStep === SELF_ENROLLMENT_STEPS.UPLOAD_DOCUMENTS ||
      currentStep === SELF_ENROLLMENT_STEPS.REMOVED_TUITION_SUMMARY
    ) {
      setSessionStepSyncLock(SELF_ENROLLMENT_STEPS.TRAINING_DATE)
      goToStep(SELF_ENROLLMENT_STEPS.TRAINING_DATE)
      return
    }

    if (currentStep === SELF_ENROLLMENT_STEPS.ADDITIONAL_INFO) {
      setSessionStepSyncLock(SELF_ENROLLMENT_STEPS.LOCATION_SELECTION)
      goToStep(SELF_ENROLLMENT_STEPS.LOCATION_SELECTION)
      return
    }

    if (currentStep <= SELF_ENROLLMENT_STEPS.ELIGIBILITY) {
      resetAnonymousEnrollmentAndReturnToOptions()
      return
    }

    const previousStep = currentStep - 1
    setSessionStepSyncLock(previousStep)
    goToStep(previousStep)
  }, [
    consumeUploadDocumentsSkipped,
    currentStep,
    enrollmentSessionToken,
    goToStep,
    queryClient,
    resetAnonymousEnrollmentAndReturnToOptions,
    setSessionStepSyncLock,
    suppressNextSessionStepSync
  ])
}
